import { useState, useEffect } from 'react'
import {
  MdBookOnline,
  MdAdd,
  MdRefresh,
  MdPerson,
  MdFlight,
  MdBusiness,
  MdAttachMoney,
  MdSchedule,
  MdCheckCircle,
  MdCancel,
  MdPayment,
} from 'react-icons/md'
import { useDispatch } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import {
  Button,
  LoadingSpinner,
  SearchBar,
  FilterBar,
  StatCard,
  EmptyState,
} from '../../components/ui'
import { useBooking } from '../../hooks/useBooking'
import { useWebsocket } from '../../hooks/useWebsocket'
import BookingList from '../../modules/bookings/BookingList'
import { addSubscription, removeSubscription } from '../../state/websocket.slice'
import { getWebsocketChannel } from '../../utils/websocket'

function BookingPage() {
  const dispatch = useDispatch()
  const [bookings, setBookings] = useState([])
  const [isLoading, setIsLoading] = useState(true)
  const [searchTerm, setSearchTerm] = useState('')
  const [statusFilter, setStatusFilter] = useState('all')
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all')
  const [flightFilter, setFlightFilter] = useState('all')
  const { getBookingsAsync, isGetBookingsLoading } = useBooking()
  const { subscribeToChannel, unsubscribeFromChannel } = useWebsocket()
  const navigate = useNavigate()
  const [stats, setStats] = useState({
    total: 0,
    confirmed: 0,
    pending: 0,
    cancelled: 0,
    completed: 0,
    totalRevenue: 0,
    averagePrice: 0,
    flights: 0,
    airlines: 0,
    passengers: 0,
    paid: 0,
    pendingPayment: 0,
    failed: 0,
  })

  const loadBookings = async () => {
    try {
      setIsLoading(true)
      const bookingsData = await getBookingsAsync()
      setBookings(bookingsData || [])

      // Calculate statistics
      const total = bookingsData.length
      const confirmed = bookingsData.filter((b) => b.status === 'confirmed').length
      const pending = bookingsData.filter((b) => b.status === 'pending').length
      const cancelled = bookingsData.filter((b) => b.status === 'cancelled').length
      const completed = bookingsData.filter((b) => b.status === 'completed').length

      const totalRevenue = bookingsData.reduce((sum, booking) => sum + (booking.price || 0), 0)
      const averagePrice = total > 0 ? totalRevenue / total : 0

      const paid = bookingsData.filter((b) => b.paymentStatus === 'paid').length
      const pendingPayment = bookingsData.filter((b) => b.paymentStatus === 'pending').length
      const failed = bookingsData.filter((b) => b.paymentStatus === 'failed').length

      // Get unique flights, airlines, and passengers
      const uniqueFlights = new Set(bookingsData.map((b) => b.flight?._id).filter(Boolean))
      const uniqueAirlines = new Set(
        bookingsData.map((b) => b.flight?.airline?._id).filter(Boolean),
      )
      const uniquePassengers = new Set(
        bookingsData.flatMap((b) => b.passengers?.map((p) => p.passenger?._id)).filter(Boolean),
      )

      setStats({
        total,
        confirmed,
        pending,
        cancelled,
        completed,
        totalRevenue,
        averagePrice,
        flights: uniqueFlights.size,
        airlines: uniqueAirlines.size,
        passengers: uniquePassengers.size,
        paid,
        pendingPayment,
        failed,
      })
    } catch (error) {
      console.error('Error loading bookings:', error)
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    loadBookings()

    const channelName = getWebsocketChannel().bookingList

    if (channelName) {
      subscribeToChannel(channelName)
      dispatch(addSubscription(channelName))
    }

    return () => {
      if (channelName) {
        unsubscribeFromChannel(channelName)
        dispatch(removeSubscription(channelName))
      }
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const getFilteredBookings = () => {
    let filtered = bookings

    // Status filter
    if (statusFilter !== 'all') {
      filtered = filtered.filter((b) => b.status === statusFilter)
    }

    // Payment status filter
    if (paymentStatusFilter !== 'all') {
      filtered = filtered.filter((b) => b.paymentStatus === paymentStatusFilter)
    }

    // Flight filter
    if (flightFilter !== 'all') {
      filtered = filtered.filter((b) => b.flight?.number === flightFilter)
    }

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(
        (b) =>
          b.bookingNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          b.flight?.number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          b.flight?.airline?.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
          b.passengers?.some(
            (p) =>
              p.passenger?.firstName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
              p.passenger?.lastName?.toLowerCase().includes(searchTerm.toLowerCase()),
          ),
      )
    }

    return filtered
  }

  const filteredBookings = getFilteredBookings()

  // Get unique flights for filters
  const uniqueFlights = [...new Set(bookings.map((b) => b.flight?.number).filter(Boolean))]

  const handleAddBooking = () => {
    navigate('/bookings/new')
  }

  const handleRefresh = () => {
    loadBookings()
  }

  const handleViewBooking = (booking) => {
    navigate(`/bookings/${booking._id}`)
  }

  const handleEditBooking = (booking) => {
    navigate(`/bookings/${booking._id}/edit`)
  }

  const handleDeleteBooking = (booking) => {
    // TODO: Implement delete confirmation modal
    console.log('Delete booking:', booking._id)
  }

  const formatCurrency = (amount, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: currency,
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount)
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  return (
    <div className="max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div className="text-left">
            <h1 className="text-2xl font-bold text-neutral-900 dark:text-white mb-2">
              Booking Management
            </h1>
            <p className="text-neutral-600 dark:text-neutral-400">
              Monitor and manage passenger bookings and reservations
            </p>
          </div>
          <div className="flex items-center space-x-3">
            <Button
              variant="ghost"
              onClick={handleRefresh}
              disabled={isGetBookingsLoading}
              className="flex items-center space-x-2"
            >
              <MdRefresh className={isGetBookingsLoading ? 'animate-spin' : ''} />
              <span>Refresh</span>
            </Button>
            <Button
              variant="primary"
              onClick={handleAddBooking}
              className="flex items-center space-x-2"
            >
              <MdAdd className="text-lg" />
              <span>Add Booking</span>
            </Button>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Bookings"
          value={stats.total}
          subtitle="Reservations"
          icon={MdBookOnline}
          iconBgColor="bg-primary-100 dark:bg-primary-900"
          iconColor="text-primary-600 dark:text-primary-400"
        />
        <StatCard
          title="Confirmed"
          value={stats.confirmed}
          subtitle="Active bookings"
          icon={MdCheckCircle}
          iconBgColor="bg-success-100 dark:bg-success-900"
          iconColor="text-success-600 dark:text-success-400"
        />
        <StatCard
          title="Pending"
          value={stats.pending}
          subtitle="Awaiting confirmation"
          icon={MdSchedule}
          iconBgColor="bg-warning-100 dark:bg-warning-900"
          iconColor="text-warning-600 dark:text-warning-400"
        />
        <StatCard
          title="Cancelled"
          value={stats.cancelled}
          subtitle="Cancelled bookings"
          icon={MdCancel}
          iconBgColor="bg-error-100 dark:bg-error-900"
          iconColor="text-error-600 dark:text-error-400"
        />
      </div>

      {/* Additional Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
          title="Total Revenue"
          value={formatCurrency(stats.totalRevenue)}
          subtitle="Total earnings"
          icon={MdAttachMoney}
          iconBgColor="bg-success-100 dark:bg-success-900"
          iconColor="text-success-600 dark:text-success-400"
        />
        <StatCard
          title="Average Price"
          value={formatCurrency(stats.averagePrice)}
          subtitle="Per booking"
          icon={MdAttachMoney}
          iconBgColor="bg-info-100 dark:bg-info-900"
          iconColor="text-info-600 dark:text-info-400"
        />
        <StatCard
          title="Paid Bookings"
          value={stats.paid}
          subtitle="Payment completed"
          icon={MdPayment}
          iconBgColor="bg-success-100 dark:bg-success-900"
          iconColor="text-success-600 dark:text-success-400"
        />
        <StatCard
          title="Pending Payment"
          value={stats.pendingPayment}
          subtitle="Awaiting payment"
          icon={MdPayment}
          iconBgColor="bg-warning-100 dark:bg-warning-900"
          iconColor="text-warning-600 dark:text-warning-400"
        />
      </div>

      {/* More Statistics */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <StatCard
          title="Active Flights"
          value={stats.flights}
          subtitle="With bookings"
          icon={MdFlight}
          iconBgColor="bg-purple-100 dark:bg-purple-900"
          iconColor="text-purple-600 dark:text-purple-400"
        />
        <StatCard
          title="Airlines"
          value={stats.airlines}
          subtitle="Handling bookings"
          icon={MdBusiness}
          iconBgColor="bg-indigo-100 dark:bg-indigo-900"
          iconColor="text-indigo-600 dark:text-indigo-400"
        />
        <StatCard
          title="Passengers"
          value={stats.passengers}
          subtitle="With bookings"
          icon={MdPerson}
          iconBgColor="bg-pink-100 dark:bg-pink-900"
          iconColor="text-pink-600 dark:text-pink-400"
        />
      </div>

      {/* Filters and Search */}
      <div className="bg-surface-light dark:bg-surface-dark rounded-xl shadow-md dark:shadow-md p-6 mb-6">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <h3 className="text-lg font-semibold text-neutral-900 dark:text-white">
            Bookings ({filteredBookings.length})
          </h3>
          <div className="flex flex-col sm:flex-row sm:items-center gap-4">
            <SearchBar
              searchTerm={searchTerm}
              onSearchChange={(e) => setSearchTerm(e.target.value)}
              placeholder="Search bookings..."
            />
            <div className="flex items-center space-x-3">
              <FilterBar
                filterValue={statusFilter}
                onFilterChange={(e) => setStatusFilter(e.target.value)}
                filterOptions={[
                  { value: 'all', label: 'All Status' },
                  { value: 'confirmed', label: `Confirmed (${stats.confirmed})` },
                  { value: 'pending', label: `Pending (${stats.pending})` },
                  { value: 'cancelled', label: `Cancelled (${stats.cancelled})` },
                  { value: 'completed', label: `Completed (${stats.completed})` },
                ]}
              />
              <FilterBar
                filterValue={paymentStatusFilter}
                onFilterChange={(e) => setPaymentStatusFilter(e.target.value)}
                filterOptions={[
                  { value: 'all', label: 'All Payment Status' },
                  { value: 'paid', label: `Paid (${stats.paid})` },
                  { value: 'pending', label: `Pending (${stats.pendingPayment})` },
                  { value: 'failed', label: `Failed (${stats.failed})` },
                ]}
              />
              <FilterBar
                filterValue={flightFilter}
                onFilterChange={(e) => setFlightFilter(e.target.value)}
                filterOptions={[
                  { value: 'all', label: 'All Flights' },
                  ...uniqueFlights.map((flight) => ({
                    value: flight,
                    label: flight,
                  })),
                ]}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Booking List */}
      {filteredBookings.length === 0 && !isLoading ? (
        <EmptyState
          icon={MdBookOnline}
          title="No bookings found"
          description={
            searchTerm ||
            statusFilter !== 'all' ||
            paymentStatusFilter !== 'all' ||
            flightFilter !== 'all'
              ? 'No bookings match your current filters. Try adjusting your search criteria.'
              : 'Get started by adding your first booking reservation.'
          }
          action={
            searchTerm ||
            statusFilter !== 'all' ||
            paymentStatusFilter !== 'all' ||
            flightFilter !== 'all' ? (
              <Button
                variant="primary"
                onClick={() => {
                  setSearchTerm('')
                  setStatusFilter('all')
                  setPaymentStatusFilter('all')
                  setFlightFilter('all')
                }}
              >
                Clear Filters
              </Button>
            ) : (
              <Button variant="primary" onClick={handleAddBooking}>
                Add Booking
              </Button>
            )
          }
        />
      ) : (
        <BookingList
          bookings={filteredBookings}
          onViewBooking={handleViewBooking}
          onEditBooking={handleEditBooking}
          onDeleteBooking={handleDeleteBooking}
        />
      )}
    </div>
  )
}

export default BookingPage
